/**
 * Build typed `VaultDocumentAttributeValueInput`s from the presentational
 * (string-keyed) attribute values captured on the upload form. The presentational
 * model carries all values as strings keyed by attribute key/name; the mutation
 * needs a backend `attributeId` plus the value in the column matching the
 * attribute's type. The caller supplies the `attributeId` + `dataType` per value
 * (resolved from the global catalog / space-local ids).
 */
import { dataTypeToValueType } from "../Space/mappers";
import type { AttrDataType } from "#common/types/vault";
import type { VaultDocumentAttributeValueInput } from "#graphql/supergraphTypes";

export interface AttributeValueSpec {
  attributeId: string;
  dataType: AttrDataType;
  /** The presentational (string) value from the upload form. */
  value: string;
  /**
   * Picked ids for an id-shaped attribute (reference / single-select /
   * multi-select). When present these route to `valueIds` per DV-466; absent,
   * the value falls back to `valueText` (upload path, which persists the display
   * name). `value` still carries the display label for the empty-value check.
   */
  ids?: string[];
  /**
   * This empty value is a DELIBERATE clear, not a field the user left alone.
   *
   * Empty specs are normally dropped: on upload an untouched field must not be
   * sent, and `updateVaultDocument` upserts, so sending nothing for it is exactly
   * right. But an edit that empties a value needs the empty column ON the wire,
   * or the save silently keeps the old value. Only meaningful for scalar types —
   * an id-shaped attribute cannot be cleared at all (the backend requires a
   * non-empty `valueIds`), so a cleared one is still dropped here.
   */
  clear?: boolean;
}

/** Value types whose selection persists as ids (`valueIds`) rather than text. */
const ID_SHAPED_VALUE_TYPES = new Set(["reference", "single_select", "multi_select"]);

/**
 * Does this attribute's value persist as picked IDS rather than a scalar?
 *
 * The one place that answers this. Callers used to keep their own `AttrDataType`-keyed
 * copy of the set, which is two places to update when the next id-shaped type lands
 * and nothing to catch them drifting apart.
 *
 * NOT the inverse of {@link isClearableDataType}: that one is text-only, so Date and
 * Number are neither clearable NOR id-shaped.
 */
export const isIdShapedDataType = (dataType: AttrDataType): boolean =>
  ID_SHAPED_VALUE_TYPES.has(dataTypeToValueType(dataType));

/** Strings that map to a `true` boolean (Yes/No attributes render "Yes"/"No"). */
const TRUE_VALUES = new Set(["yes", "true", "1"]);

/**
 * Shared truthy test for a presentational boolean value. Used both here (upload
 * build) and by the search filter mapper (`boolean`) so the two paths agree on
 * exactly which strings count as `true` (`yes` / `true` / `1`, case-insensitive).
 */
export const isTruthyAttributeValue = (value: string): boolean =>
  TRUE_VALUES.has(value.trim().toLowerCase());

/** Route one presentational value into the correct typed input column. */
export const buildAttributeValueInput = (
  spec: AttributeValueSpec,
): VaultDocumentAttributeValueInput => {
  const valueType = dataTypeToValueType(spec.dataType);
  // Id-shaped attributes persist their picked ids in `valueIds` (DV-466) when
  // ids were supplied; without them they fall through to the text column below,
  // preserving the display-name-as-text behavior.
  //
  // Display names are not persisted. On read:
  // - reference attributes resolve via federated `valueArenaList` /
  //   `valueEntityList` / `valueLpUserList`
  // - select attributes resolve via nested `attribute.options`, falling back
  //   to the Attribute catalog (`optionValueById`)
  //
  // Sending `valueText` alongside `valueIds` is NOT a workaround: the subgraph
  // enforces exactly one value form per assignment ("an attribute value must
  // populate exactly one of value_text, value_number, value_date, value_boolean,
  // or valueIds") and rejects the write outright.
  if (ID_SHAPED_VALUE_TYPES.has(valueType) && spec.ids && spec.ids.length > 0) {
    return { attributeId: spec.attributeId, valueIds: spec.ids };
  }
  switch (valueType) {
    case "number":
      // Decimal scalar is a string on the wire.
      return { attributeId: spec.attributeId, valueNumber: spec.value };
    case "date":
      return { attributeId: spec.attributeId, valueDate: spec.value };
    case "boolean":
      return {
        attributeId: spec.attributeId,
        valueBoolean: isTruthyAttributeValue(spec.value),
      };
    default:
      // text and id-shaped values without ids fall back to value_text.
      return { attributeId: spec.attributeId, valueText: spec.value };
  }
};

/**
 * Can this attribute's value be emptied on the wire? Only a TEXT one.
 *
 * `updateVaultDocument` upserts, so a clear has to travel as an empty column — and
 * only `valueText` has an empty form that survives the trip:
 *
 * - `valueDate` / `valueNumber` are the `Date` / `Decimal` custom scalars, so `""`
 *   fails scalar parsing at the gateway and rejects the WHOLE mutation, taking
 *   every other attribute in the same edit down with it.
 * - `valueBoolean` has no empty member: `""` coerces to `false`, i.e. the field
 *   would read "No" after the user emptied it — a value they never picked.
 * - `valueIds` (reference / select) must be non-empty, per the subgraph.
 *
 * Everything except text therefore has no clear to express, and the UI blocks it
 * rather than writing something the user didn't choose. Widening this needs a
 * backend affordance for "no value", not a client-side coercion.
 */
export const isClearableDataType = (dataType: AttrDataType): boolean =>
  dataTypeToValueType(dataType) === "text";

/**
 * Build the input list, dropping empty values (nothing to persist). The empty
 * check is on the trimmed value, so a whitespace-only entry (e.g. "   ") is
 * dropped rather than sent as e.g. `valueNumber: "   "`.
 *
 * A spec marked {@link AttributeValueSpec.clear} is the exception: an edit that
 * empties a scalar has to put the empty column on the wire, because
 * `updateVaultDocument` upserts and an omitted attribute keeps its current value.
 * An id-shaped clear is still dropped — see {@link isClearableDataType} — so the
 * caller is responsible for not offering a clear it can't persist.
 */
export const buildAttributeValueInputs = (
  specs: AttributeValueSpec[],
): VaultDocumentAttributeValueInput[] =>
  specs
    .filter(
      (s) =>
        (s.ids && s.ids.length > 0) ||
        (s.value != null && s.value.trim() !== "") ||
        (s.clear === true && isClearableDataType(s.dataType)),
    )
    .map(buildAttributeValueInput);
